import { useState } from 'react'
import type { StudioOptionsResponseDto, StudioVariantDto } from '@lunary/api-client'
import { useCards } from '../../shared/queries/cards'
import { usePlayground, useStudioOptions } from './queries'
import { VariantConfig } from './variant-config'
import { VariantResult } from './variant-result'
import { Select } from '@lunary/ui-web/select'
import styles from './studio.module.css'

function defaults(o: StudioOptionsResponseDto): [StudioVariantDto, StudioVariantDto] {
  const a = { model: o.default_model || o.models[0] || '', persona_version: o.active_persona, system_version: o.active_system }
  const prevPersona = [...o.persona_versions].reverse().find((v) => v < o.active_persona)
  const otherModel = o.models.find((m) => m !== a.model)
  const b = prevPersona !== undefined ? { ...a, persona_version: prevPersona } : { ...a, model: otherModel ?? a.model }
  return [a, b]
}

export function Playground() {
  const options = useStudioOptions()
  const { cards } = useCards()
  const majors = cards.filter((c) => c.arcana === 'major').sort((x, y) => x.number - y.number)
  const generate = usePlayground()
  const [cardId, setCardId] = useState('')
  const [compare, setCompare] = useState(true)
  const [variants, setVariants] = useState<[StudioVariantDto, StudioVariantDto] | null>(null)
  const [showFewshot, setShowFewshot] = useState(false)

  if (options.isPending) return <div className="spinner" aria-label="Загрузка" />
  if (options.isError) return <p className="error-box">Не удалось загрузить настройки Студии.</p>
  const o = options.data
  if (o.models.length === 0) return <p className="error-box">Модель не настроена: задайте LLM_MODEL_DAILY.</p>
  const [a, b] = variants ?? defaults(o)
  const selectedCard = cardId || majors[0]?.id || ''

  const run = () =>
    generate.mutate({ card_id: selectedCard, variants: compare ? [a, b] : [a] })

  const result = generate.data

  return (
    <div>
      <div className="card-panel">
        <div className={styles.controls}>
          <label>
            Карта
            <Select
              aria-label="Карта"
              className={styles.wide}
              value={selectedCard}
              options={majors.map((c) => ({ value: c.id, label: `${c.number}. ${c.name}` }))}
              onChange={setCardId}
            />
          </label>
          <label className={styles.check}>
            <input type="checkbox" checked={compare} onChange={(e) => setCompare(e.target.checked)} />
            Сравнить два варианта
          </label>
        </div>
        <div className={compare ? styles.configs : styles.configsSingle}>
          <VariantConfig label="Вариант A" value={a} options={o} onChange={(next) => setVariants([next, b])} />
          {compare && (
            <VariantConfig label="Вариант B" value={b} options={o} onChange={(next) => setVariants([a, next])} />
          )}
        </div>
        <div className="row" style={{ marginTop: 16 }}>
          <button disabled={generate.isPending || !selectedCard} onClick={run}>
            {generate.isPending ? 'Генерирую…' : compare ? 'Сгенерировать A и B' : 'Сгенерировать'}
          </button>
          <span className="muted" style={{ fontSize: 13 }}>
            Ответы в песочнице никуда не уходят, пока вы не сохраните эталон.
          </span>
        </div>
        {generate.error && (
          <p className="error-box">{generate.error instanceof Error ? generate.error.message : 'Ошибка генерации'}</p>
        )}
      </div>

      {result && (
        <div aria-live="polite">
          <div className={styles.inputBox}>
            <strong>{result.card.name}</strong>
            <span className="muted"> · что видит модель: значение и ключевые слова карты</span>
            {result.fewshot_used.length > 0 && (
              <button className="secondary" style={{ marginLeft: 12 }} onClick={() => setShowFewshot((v) => !v)}>
                Эталоны в подсказке ({result.fewshot_used.length})
              </button>
            )}
            {result.fewshot_used.length === 0 && (
              <span className="muted"> · эталонов для этой карты пока нет</span>
            )}
            {showFewshot &&
              result.fewshot_used.map((text, i) => (
                <div key={i} className="transcript" style={{ marginTop: 8 }}>
                  {text}
                </div>
              ))}
          </div>
          <div className={result.variants.length > 1 ? styles.results : styles.resultsSingle}>
            {result.variants.map((v, i) => (
              <VariantResult
                key={`${generate.submittedAt}-${i}`}
                label={i === 0 ? 'Вариант A' : 'Вариант B'}
                cardId={result.card.id}
                variant={v}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
